import AgoraRTC from 'agora-rtc-sdk-ng';

import AgoraAdapterBase from './AgoraAdapterBase';

/**
 * AgoraPublisherAdapter provides a wrapper around the Agora SDK for hosting live
 * streams and coordinating messaging.
 */
export default class AgoraPublisherAdapter extends AgoraAdapterBase {
  constructor(streamType = 'agora') {
    super(streamType);
    this.localStream = null;
    this.localAudioTrack = null;
    this.localVideoTrack = null;
    this.startTime = null;
    this.devices = {
      audioDevices: [],
      videoDevices: [],
      defaultAudioDevice: null,
      defaultVideoDevice: null,
    };
    this.tickInterval = null;
  }

  /**
   * Initialize the adapter with configuration
   * @param {Object} options - Configuration options
   * @param {Function} errorCallback - Error callback
   */
  async init(options, errorCallback) {
    try {
      // Initialize common components from base class
      await this.initCommon(options);
      this.log('Initializing AgoraPublisherAdapter');

      // Set publisher-specific callback handlers
      this.onLocalStreamInit = options.onLocalStreamInit || (() => {});
      this.onStreamStart = options.onStreamStart || (() => {});
      this.onStreamTick = options.onStreamTick || (() => {});
      this.onServerDataValidateError = options.onServerDataValidateError || (() => {});
      this.onDevicesReadyCallback = options.onDevicesReadyCallback || (() => {});
      this.onInfoStreamer = options.onInfoStreamer || (() => {});

      // Video settings
      this.videoFrameRate = options.videoFrameRate || 30;
      this.videoSave = options.videoSave || false;

      // Create the Agora RTC client as host
      this.client = AgoraRTC.createClient({
        mode: 'live',
        codec: 'vp8',
        role: 'host',
      });

      // Setup publisher-specific client event listeners
      this.setupPublisherClientListeners();

      // Enumerate devices
      await this.loadDevices();
    } catch (error) {
      this.error('Initialization error:', error);
      if (errorCallback) {
        errorCallback(error);
      } else {
        this.onStreamError(`Failed to initialize: ${error.message}`);
      }
    }
  }

  /**
   * Set up event listeners specific to publisher
   */
  setupPublisherClientListeners() {
    // Call base setup first
    super.setupClientListeners();

    this.client.on('connection-state-change', (curState) => {
      if (curState === 'DISCONNECTED' && this.startTime) {
        this.cleanUp();
      }
    });

    this.client.on('user-published', (user, mediaType) => {
      this.log(`User ${user.uid} published ${mediaType}`);
    });

    this.client.on('user-joined', (user) => {
      this.log(`User ${user.uid} joined`);
    });
  }

  /**
   * Load available media devices
   */
  async loadDevices() {
    try {
      // Get all available devices
      const devices = await AgoraRTC.getDevices();

      // Filter devices by kind
      const audioDevices = devices.filter((device) => device.kind === 'audioinput');
      const videoDevices = devices.filter((device) => device.kind === 'videoinput');

      // Set default devices
      const defaultAudioDevice = audioDevices.length > 0 ? audioDevices[0] : null;
      const defaultVideoDevice = videoDevices.length > 0 ? videoDevices[0] : null;

      this.devices = {
        audioDevices,
        videoDevices,
        defaultAudioDevice,
        defaultVideoDevice,
      };

      this.log('Devices loaded:', this.devices);

      // Create local tracks with default devices
      await this.createLocalTracks();

      // Notify that devices are ready
      if (this.onDevicesReadyCallback) {
        this.onDevicesReadyCallback();
      }
    } catch (error) {
      this.error('Error loading devices:', error);
      this.onStreamError(`Failed to access camera or microphone: ${error.message}`);
    }
  }

  /**
   * Create local audio and video tracks
   */
  async createLocalTracks() {
    try {
      // Release previous tracks if they exist
      if (this.localAudioTrack) {
        this.localAudioTrack.close();
      }
      if (this.localVideoTrack) {
        this.localVideoTrack.close();
      }

      // Create new tracks
      const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks(
        { deviceId: this.devices.defaultAudioDevice?.deviceId },
        {
          deviceId: this.devices.defaultVideoDevice?.deviceId,
          encoderConfig: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: this.videoFrameRate,
            bitrateMin: 1000,
            bitrateMax: 2500,
          },
        },
      );

      this.localAudioTrack = audioTrack;
      this.localVideoTrack = videoTrack;

      // Create virtual stream for local preview
      this.localStream = new MediaStream([
        this.localAudioTrack.getMediaStreamTrack(),
        this.localVideoTrack.getMediaStreamTrack(),
      ]);

      // Play video locally
      if (this.videoElId) {
        const videoElement = document.getElementById(this.videoElId);
        if (videoElement) {
          videoElement.srcObject = this.localStream;
          videoElement.muted = true; // Mute local preview to prevent feedback
          await videoElement.play().catch((error) => {
            this.error('Error playing local video:', error);
          });
        }
      }

      // Trigger callback for local stream initialization
      if (this.onLocalStreamInit) {
        this.onLocalStreamInit();
      }
    } catch (error) {
      this.error('Error creating local tracks:', error);
      this.onStreamError(`Failed to create local media tracks: ${error.message}`);
    }
  }

  /**
   * Get available devices
   * @returns {Object} Object containing audio and video devices
   */
  getDevices() {
    return this.devices;
  }

  /**
   * Switch audio/video devices
   * @param {string|boolean} audioDeviceId - New audio device ID or false
   * @param {string|boolean} videoDeviceId - New video device ID or false
   */
  async switchDevices(audioDeviceId, videoDeviceId) {
    try {
      // Change audio device if specified
      if (audioDeviceId !== false && this.localAudioTrack) {
        await this.localAudioTrack.setDevice(audioDeviceId);
        this.log('Switched audio device to:', audioDeviceId);
      }

      // Change video device if specified
      if (videoDeviceId !== false && this.localVideoTrack) {
        await this.localVideoTrack.setDevice(videoDeviceId);
        this.log('Switched video device to:', videoDeviceId);

        // Update the local stream with the new video track
        this.localStream = new MediaStream([
          this.localAudioTrack.getMediaStreamTrack(),
          this.localVideoTrack.getMediaStreamTrack(),
        ]);

        // Update video element
        if (this.videoElId) {
          const videoElement = document.getElementById(this.videoElId);
          if (videoElement) {
            videoElement.srcObject = this.localStream;
          }
        }
      }
    } catch (error) {
      this.error('Error switching devices:', error);
      this.showErrorMessage(`Failed to switch devices: ${error.message}`);
    }
  }

  /**
   * Mute/unmute audio or video device
   * @param {string} type - 'audio' or 'video'
   * @param {boolean} enabled - Whether to enable the track
   */
  muteDevice(type, enabled) {
    try {
      if (type === 'audio' && this.localAudioTrack) {
        if (enabled) {
          this.localAudioTrack.setEnabled(true);
          this.log('Audio unmuted');
        } else {
          this.localAudioTrack.setEnabled(false);
          this.log('Audio muted');
        }
      } else if (type === 'video' && this.localVideoTrack) {
        if (enabled) {
          this.localVideoTrack.setEnabled(true);
          this.log('Video unmuted');
        } else {
          this.localVideoTrack.setEnabled(false);
          this.log('Video muted');
        }
      }
    } catch (error) {
      this.error('Error muting device:', error);
      this.showErrorMessage(`Failed to mute device: ${error.message}`);
    }
  }

  /**
   * Start broadcasting the stream
   */
  async startStream() {
    try {
      if (!this.serverData) {
        this.onStreamError("Can't get streaming server data...");
        return;
      }

      this.log('Starting stream with config:', {
        appId: this.appId,
        roomName: this.roomName,
        token: this.token ? '***' : null,
        uid: this.uid,
      });

      // Join the room as host
      await this.client.join(this.appId, this.roomName, this.token, this.uid || null);

      // Publish local tracks
      await this.client.publish([this.localAudioTrack, this.localVideoTrack]);

      this.log('Successfully published local tracks');

      await this.connectChat();

      // Set start time and begin ticking
      this.startTime = Math.floor(Date.now() / 1000);
      this.startTickInterval();

      // Notify that stream has started
      if (this.onStreamStart) {
        this.onStreamStart(this.roomName);
      }

      this.showInfoMessage('Stream started successfully');
    } catch (error) {
      this.error('Error starting stream:', error);
      this.onStreamError(
        `Failed to start stream: ${error.message}`,
      );
    }
  }

  /**
   * Start the tick interval for stream timing.
   */
  startTickInterval() {
    this.tickInterval = setInterval(() => {
      if (this.onStreamTick && this.startTime) {
        this.onStreamTick(this.startTime);
      }
    }, 1000);
  }

  stopTickInterval() {
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
  }

  /**
   * Stop broadcasting the stream.
   */
  async stopStream() {
    try {
      this.log('Stopping stream');
      this.stopTickInterval();

      // Unpublish local tracks
      if (this.client) {
        await this.client.unpublish([
          this.localAudioTrack,
          this.localVideoTrack,
        ]);
      }

      // Use base class to clean up
      await this.cleanupCommChannel();

      // Leave the room
      if (this.client) {
        await this.client.leave();
      }

      this.startTime = null;

      // Notify that stream has ended
      if (this.onStreamEnd) {
        this.onStreamEnd();
      }

      this.showInfoMessage('Stream ended');
    } catch (error) {
      this.error('Error stopping stream:', error);

      // Still trigger onStreamEnd even if there was an error
      if (this.onStreamEnd) {
        this.onStreamEnd();
      }
    }
  }

  /**
   * Clean up resources when the stream ends.
   */
  cleanUp() {
    this.log('Cleaning up resources');

    // Clear tick interval
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }

    // Release local tracks
    if (this.localAudioTrack) {
      this.localAudioTrack.close();
      this.localAudioTrack = null;
    }

    if (this.localVideoTrack) {
      this.localVideoTrack.close();
      this.localVideoTrack = null;
    }

    this.startTime = null;

    // Notify that stream has ended
    if (this.onStreamEnd) {
      this.onStreamEnd();
    }
  }
}
